import { SIGNATURE_MAX_BYTES, SIGNATURE_EXPORT_INK } from "@shared/agreement";

/**
 * Turns an uploaded signature image into the same kind of PNG the drawing pad
 * produces: transparent background, cropped to the ink, dark strokes.
 *
 * WHY BACKGROUND REMOVAL IS NOT OPTIONAL. People upload a scan or a phone
 * photo of a signature on paper, which is opaque white. Stamped onto page 13
 * of the agreement as-is, that pastes a white rectangle over the signature
 * line and whatever text sits near it. The document would look defaced. So the
 * paper has to become transparent before the image is usable.
 *
 * HOW. Alpha is derived from luminance: bright pixels (paper) become
 * transparent, dark pixels (ink) become opaque, with a ramp between so stroke
 * antialiasing survives and the result does not look cut out with scissors.
 * The paper level is MEASURED from the image rather than assumed to be pure
 * white, because a phone photo's background is typically grey, shadowed, or
 * colour-cast, and a fixed threshold would leave a dirty haze behind.
 *
 * An image that already has real transparency (exported from a signature app,
 * say) is left alone apart from trimming, since its alpha is better than
 * anything we would infer.
 */

/** Longest edge of the processed signature. Keeps the stored PNG small. */
const MAX_EDGE = 1200;

export interface ProcessedSignature {
  dataUrl: string;
  width: number;
  height: number;
}

export class SignatureImageError extends Error {}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // The common real-world cause is an iPhone HEIC photo, which browsers
      // cannot decode to a canvas. Say something the signer can act on.
      reject(new SignatureImageError(
        "That image could not be opened. Please use a PNG or JPG. " +
        "iPhone photos saved as HEIC will not work, so choose 'Most Compatible' " +
        "in your camera settings or export the picture as a JPG first.",
      ));
    };
    img.src = url;
  });
}

/**
 * Estimates the paper luminance as a high percentile of the histogram.
 *
 * A mean would be dragged down by the ink; the maximum would be dragged up by
 * a single specular highlight. The 85th percentile lands on the paper itself
 * for both a clean scan and a badly-lit photo.
 *
 * Exported for testing: this is the number the whole background removal hinges
 * on, and getting it wrong leaves either a grey haze or an erased signature.
 */
export function estimatePaperLuminance(lum: ArrayLike<number>): number {
  const hist = new Uint32Array(256);
  for (let i = 0; i < lum.length; i++) hist[lum[i]]++;
  const target = lum.length * 0.85;
  let seen = 0;
  for (let v = 0; v < 256; v++) {
    seen += hist[v];
    if (seen >= target) return v;
  }
  return 255;
}

/** True when the source image already carries meaningful transparency. */
export function hasRealTransparency(px: ArrayLike<number>): boolean {
  let transparent = 0;
  for (let i = 3; i < px.length; i += 4) if (px[i] < 250) transparent++;
  return transparent > (px.length / 4) * 0.05;
}

/**
 * Rewrites pixels in place: paper becomes transparent, ink becomes opaque dark.
 *
 * Pure and DOM-free so it can be tested directly. Alpha ramps between the
 * measured paper level and an ink level derived from it, which preserves stroke
 * antialiasing instead of producing hard, cut-out edges.
 */
export function removePaperBackground(px: Uint8ClampedArray | Uint8Array): void {
  const count = px.length / 4;
  const lum = new Uint8Array(count);
  for (let p = 0, i = 0; i < px.length; i += 4, p++) {
    lum[p] = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
  }
  // Floored so a photo of a signature on dark paper cannot invert the logic
  // and erase the ink instead of the background.
  const paper = Math.max(120, estimatePaperLuminance(lum));
  const inkLevel = paper * 0.55;
  const span = Math.max(1, paper - inkLevel);

  for (let p = 0, i = 0; i < px.length; i += 4, p++) {
    const a = Math.round(((paper - lum[p]) / span) * 255);
    px[i] = SIGNATURE_EXPORT_INK.r; px[i + 1] = SIGNATURE_EXPORT_INK.g; px[i + 2] = SIGNATURE_EXPORT_INK.b;
    px[i + 3] = a < 0 ? 0 : a > 255 ? 255 : a;
  }
}

/**
 * Fraction of pixels that ended up strongly opaque after processing.
 *
 * Guards against an INVERTED source: a light signature photographed on dark
 * card. The algorithm assumes dark ink on light paper, which is overwhelmingly
 * the normal case, and on an inverted image it does the opposite of what is
 * wanted, keeping the background and erasing the strokes. Left unchecked that
 * would stamp a near-solid dark rectangle over page 13 of the agreement.
 *
 * Real signatures cover a small share of their bounding area, so a very high
 * ratio means the image is inverted, far too dark, or not a signature at all.
 */
export function inkCoverageRatio(px: ArrayLike<number>): number {
  let opaque = 0;
  const count = px.length / 4;
  for (let i = 3; i < px.length; i += 4) if (px[i] > 200) opaque++;
  return count === 0 ? 0 : opaque / count;
}

/** Above this, treat the image as inverted or unusable rather than signing it. */
export const MAX_INK_COVERAGE = 0.6;

/**
 * Bounding box of everything above `alphaFloor`, or null when nothing is.
 *
 * Pure counterpart of the canvas trim below, so "did any ink survive?" is
 * testable without a browser.
 */
export function findInkBounds(
  px: ArrayLike<number>,
  width: number,
  height: number,
  alphaFloor = 8,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (px[(y * width + x) * 4 + 3] > alphaFloor) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  return maxX < 0 ? null : { minX, minY, maxX, maxY };
}

/**
 * Crops fully-transparent margins and returns a new canvas.
 * Returns null when there is no ink at all.
 */
function trim(canvas: HTMLCanvasElement, alphaFloor = 8): HTMLCanvasElement | null {
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const { width: w, height: h } = canvas;
  const data = ctx.getImageData(0, 0, w, h).data;

  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > alphaFloor) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;

  const pad = 6;
  minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);

  const out = document.createElement("canvas");
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  out.getContext("2d")?.drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

/**
 * Processes an uploaded image file into a signature PNG.
 *
 * Re-encoding through a canvas also strips EXIF and any other metadata the
 * original carried, so a phone photo's GPS location never reaches storage.
 */
export async function processSignatureUpload(file: File): Promise<ProcessedSignature> {
  const img = await loadImage(file);
  if (!img.width || !img.height) {
    throw new SignatureImageError("That image appears to be empty.");
  }

  // Downscale first, so all later per-pixel work is on a sane number of pixels.
  const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new SignatureImageError("Your browser could not process that image.");
  ctx.drawImage(img, 0, 0, w, h);

  let image: ImageData;
  try {
    image = ctx.getImageData(0, 0, w, h);
  } catch {
    throw new SignatureImageError("Your browser blocked reading that image. Please try a different file.");
  }
  const px = image.data;

  // An image that already has real transparency (exported from a signature
  // app, say) keeps its own alpha: it is better than anything we would infer.
  if (!hasRealTransparency(px)) {
    removePaperBackground(px);

    // Refuse an inverted or near-black image rather than stamping a dark
    // rectangle onto the agreement. Better a clear error than a defaced
    // contract the signer does not notice until it is filed.
    if (inkCoverageRatio(px) > MAX_INK_COVERAGE) {
      throw new SignatureImageError(
        "That image is too dark to use. Please upload a photo of your signature " +
        "written in dark ink on light paper, with the paper well lit.",
      );
    }
    ctx.putImageData(image, 0, 0);
  }

  const trimmed = trim(canvas);
  if (!trimmed) {
    throw new SignatureImageError(
      "No signature was found in that image. Please upload a clear picture of your signature on a light background.",
    );
  }

  // Shrink further if the PNG is still too large for the storage contract.
  let out = trimmed;
  let dataUrl = out.toDataURL("image/png");
  for (let attempt = 0; attempt < 4 && dataUrl.length * 0.75 > SIGNATURE_MAX_BYTES; attempt++) {
    const next = document.createElement("canvas");
    next.width = Math.max(1, Math.round(out.width * 0.7));
    next.height = Math.max(1, Math.round(out.height * 0.7));
    next.getContext("2d")?.drawImage(out, 0, 0, next.width, next.height);
    out = next;
    dataUrl = out.toDataURL("image/png");
  }

  if (dataUrl.length * 0.75 > SIGNATURE_MAX_BYTES) {
    throw new SignatureImageError("That image is too large to use. Please try a smaller or simpler picture.");
  }

  return { dataUrl, width: out.width, height: out.height };
}
